import React, { useCallback, useEffect, useState } from "react";
import apiClient from "../../utils/apiClient.js";
import NotificationModal from "../../components/NotificationModal.js";
import useNotification from "../../hooks/useNotification.js";
import { CATEGORIES } from "../../utils/calculateTotal.js";
import useAutoRefresh from "../../hooks/useAutoRefresh.js";
import Spinner from "../../components/Spinner.js";
import Pagination from "../../components/Pagination.js";
import "./Admin.css";

const STATUSES = ["new", "contacted", "follow_up_required", "converted", "closed"];
const PER_PAGE = 10;

const categoryLabel = (key) => CATEGORIES.find((c) => c.key === key)?.label || key;
const formatCategories = (categories) => (Array.isArray(categories) ? categories : [categories]).map(categoryLabel).join(", ");
const formatDetails = (details) =>
  Object.entries(details || {})
    .flatMap(([category, fields]) =>
      Object.entries(fields || {})
        .filter(([, value]) => value)
        .map(([key, value]) => `${category}.${key}: ${value}`)
    )
    .join(", ") || "—";

const formatDateTime = (value) => new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const QuoteLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { notification, notify, dismiss } = useNotification();
  const [savingId, setSavingId] = useState(null);

  // silent=true (background auto-refresh) skips the loading/error state changes so a live poll
  // never flashes "Loading…" over an already-rendered table or clobbers it with an error banner
  // over a transient network blip.
  const load = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const { data } = await apiClient.get("/quote-leads", { params: { status, q, page, perPage: PER_PAGE } });
      setLeads(data.data);
      setTotal(data.total);
    } catch (err) {
      if (!silent) setError("Could not load quote leads.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [status, q, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Keeps this page live without a manual reload: refetches when the tab regains focus and on a
  // background interval, so an admin who leaves it open doesn't keep looking at a stale snapshot.
  useAutoRefresh(useCallback(() => load(true), [load]));

  const handleStatusChange = async (id, newStatus) => {
    setSavingId(id);
    try {
      await apiClient.post(`/quote-leads/${id}/status`, { status: newStatus });
      setLeads((prev) => prev.map((lead) => (lead.id === id ? { ...lead, status: newStatus } : lead)));
      notify("success", "Status updated", `Lead marked as ${newStatus.replace(/_/g, " ")}.`);
    } catch (err) {
      notify("error", "Could not update status", "Please try again.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Quote Leads</h1>
        <p>{total} total</p>
      </div>

      <div className="admin-filters">
        <input
          type="search"
          placeholder="Search by name, email or phone…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, " ")}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="admin-error">{error}</p>}

      {loading ? (
        <Spinner label="Loading quote leads…" />
      ) : leads.length === 0 ? (
        <p>No quote leads found.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>S.N.</th>
                <th>Customer</th>
                <th>Contact</th>
                <th>Category</th>
                <th>Details</th>
                <th>Quoted Total</th>
                <th>Submitted</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead, index) => (
                <tr key={lead.id}>
                  <td>{(page - 1) * PER_PAGE + index + 1}</td>
                  <td>{lead.full_name}</td>
                  <td>
                    <div>{lead.email}</div>
                    <div>{lead.phone}</div>
                  </td>
                  <td>{formatCategories(lead.quote_category)}</td>
                  <td className="admin-details-cell">{formatDetails(lead.details)}</td>
                  <td>${lead.quoted_total}</td>
                  <td>{formatDateTime(lead.created_at)}</td>
                  <td>
                    <select
                      value={lead.status}
                      disabled={savingId === lead.id}
                      onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} perPage={PER_PAGE} total={total} onPageChange={setPage} />
        </div>
      )}

      {notification && (
        <NotificationModal
          variant={notification.variant}
          title={notification.title}
          message={notification.message}
          onClose={dismiss}
        />
      )}
    </div>
  );
};

export default QuoteLeadsPage;
